import { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import Loader from "../UI/Loader";

const isYoutubeUrl = (url) =>
  url?.includes("youtube.com") || url?.includes("youtu.be");

const getYoutubeEmbedUrl = (url) => {
  if (!url) return null;
  const match = url.match(/v=([^&]+)/) || url.match(/youtu\.be\/([^?]+)/);
  const videoId = match?.[1];
  if (!videoId) return null;
  return `https://www.youtube.com/embed/${videoId}`;
};

/**
 * Formulario reutilizable para subir/actualizar un video (archivo o URL).
 *
 * @param {string} currentVideoUrl - URL del video actualmente guardado.
 * @param {boolean} existe - Si ya hay un registro guardado (habilita el botón eliminar).
 * @param {boolean} saving
 * @param {boolean} deleting
 * @param {Function} onSubmit - async ({ file, url }) => void.
 * @param {Function} [onDelete] - async () => void. Si no se pasa, no se muestra el botón.
 * @param {string} submitLabel - Texto del botón principal ("Guardar video" / "Actualizar video").
 */
const VideoUploaderForm = ({
  currentVideoUrl,
  existe,
  saving,
  deleting,
  onSubmit,
  onDelete,
  submitLabel = "Guardar video",
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);
  const [urlValue, setUrlValue] = useState("");
  const [localError, setLocalError] = useState(null);

  const hasFile = Boolean(selectedFile);
  const hasUrl = Boolean(urlValue.trim());

  useEffect(() => {
    return () => {
      if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
    };
  }, [filePreviewUrl]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    setLocalError(null);
    if (!file) return;
    if (hasUrl) setUrlValue("");
    if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
    setSelectedFile(file);
    setFilePreviewUrl(URL.createObjectURL(file));
  };

  const handleUrlChange = (e) => {
    setLocalError(null);
    if (hasFile) {
      setSelectedFile(null);
      if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
      setFilePreviewUrl(null);
    }
    setUrlValue(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError(null);

    if (!hasFile && !hasUrl) {
      return setLocalError("Debes seleccionar un archivo o ingresar una URL.");
    }
    if (hasFile && hasUrl) {
      return setLocalError("Solo puedes elegir una opción: archivo o enlace.");
    }

    await onSubmit({
      file: hasFile ? selectedFile : null,
      url: hasUrl ? urlValue.trim() : null,
    });

    setSelectedFile(null);
    setFilePreviewUrl(null);
    setUrlValue("");
  };

  const previewSource = filePreviewUrl || currentVideoUrl;
  const youtubeEmbed = isYoutubeUrl(previewSource)
    ? getYoutubeEmbedUrl(previewSource)
    : null;

  return (
    <form onSubmit={handleSubmit}>
      {localError && (
        <div className="alert alert-danger py-2 mb-3 text-sm">{localError}</div>
      )}

      {existe && currentVideoUrl && onDelete && (
        <div className="alert alert-info py-2 mb-3 d-flex align-items-center justify-content-between gap-2">
          <span className="text-sm">
            <Icon icon="mdi:check-circle-outline" className="me-1" />
            Video activo.
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={onDelete}
            disabled={deleting}
          >
            {deleting ? (
              <span className="spinner-border spinner-border-sm" />
            ) : (
              <Icon icon="mdi:delete-outline" />
            )}
          </button>
        </div>
      )}

      <div className="row g-3 align-items-end">
        <div className="col-12 col-md-5">
          <label className="form-label fw-medium d-flex align-items-center gap-2">
            <Icon icon="mdi:upload" width={20} />
            Subir archivo de video
          </label>
          <input
            type="file"
            accept="video/*"
            className="form-control"
            onChange={handleFileChange}
            disabled={saving || hasUrl}
          />
        </div>

        <div className="col-12 col-md-2 d-flex align-items-center justify-content-center">
          <div className="d-flex flex-md-column align-items-center gap-2 py-1">
            <hr className="flex-grow-1 m-0" style={{ minWidth: 24 }} />
            <span
              className="badge bg-base border text-muted rounded-pill px-3 py-1 small fw-normal"
              style={{ whiteSpace: "nowrap" }}
            >
              o bien
            </span>
            <hr className="flex-grow-1 m-0" style={{ minWidth: 24 }} />
          </div>
        </div>

        <div className="col-12 col-md-5">
          <label className="form-label fw-medium d-flex align-items-center gap-2">
            <Icon icon="mdi:link-variant" width={20} />
            URL externa del video
          </label>
          <input
            type="url"
            className="form-control"
            placeholder="https://..."
            value={urlValue}
            onChange={handleUrlChange}
            disabled={saving || hasFile}
          />
        </div>
      </div>

      <p className="text-muted small mt-3 mb-0 text-center">
        Solo puedes elegir una opción: archivo o enlace.
      </p>

      {previewSource && (
        <div className="mt-4">
          <label className="form-label fw-medium d-block text-center mb-3">
            Vista previa
          </label>
          <div className="d-flex justify-content-center">
            <div
              className="ratio ratio-16x9 border rounded-3 overflow-hidden shadow-sm"
              style={{ maxWidth: "560px", width: "100%" }}
            >
              {youtubeEmbed ? (
                <iframe
                  src={youtubeEmbed}
                  title="Vista previa"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ width: "100%", height: "100%", border: 0 }}
                />
              ) : (
                <video
                  src={previewSource}
                  controls
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 d-grid">
        <button
          type="submit"
          className="btn btn-primary d-flex align-items-center justify-content-center gap-2"
          disabled={saving}
        >
          {saving ? (
            <>
              <Loader type="spinner" size="sm" delay={0} /> Guardando...
            </>
          ) : (
            <>
              <Icon icon="mdi:content-save" width={20} /> {submitLabel}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default VideoUploaderForm;
